import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { videos, categories, subCategories, type VideoItem } from '@/mocks/videos';

const categoryNames: Record<string, string> = {};
categories.forEach(c => { categoryNames[c.slug] = c.name; });

export default function CategoryPage() {
  const { slug = 'drama' } = useParams<{ slug: string }>();
  const [activeSubCategory, setActiveSubCategory] = useState<string>('全部');
  const [filteredVideos, setFilteredVideos] = useState<VideoItem[]>([]);

  const categoryName = categoryNames[slug] || '分类';
  const currentSubCategories = subCategories[slug] || [];

  const filterVideos = useCallback(() => {
    let result = videos.filter(v => v.category === slug);
    if (result.length === 0) {
      result = videos.filter(v => v.category === 'drama');
    }
    if (activeSubCategory !== '全部') {
      result = result.filter(v => v.subCategory === activeSubCategory);
    }
    setFilteredVideos(result);
  }, [slug, activeSubCategory]);

  useEffect(() => {
    setActiveSubCategory('全部');
  }, [slug]);

  useEffect(() => {
    filterVideos();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [filterVideos, slug]);

  return (
    <>
      <SEOHead
        title={`${categoryName} - 免费在线观看高清${categoryName} | 棒棒TV`}
        description={`棒棒TV提供海量高清${categoryName}免费在线观看，不卡顿免缓冲流畅播放。最新${categoryName}全集、热门${categoryName}推荐一网打尽。`}
        keywords={`${categoryName},免费${categoryName},在线观看${categoryName},高清${categoryName},最新${categoryName},热门${categoryName},棒棒TV`}
        canonicalUrl={`/category/${slug}`}
        ogType="website"
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            {/* Breadcrumb */}
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />

            {/* Page Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
              <h1 className="text-2xl font-heading font-bold text-foreground-50">
                {categoryName}
              </h1>
              <span className="text-sm text-foreground-500">
                共 {filteredVideos.length} 部
              </span>
            </div>

            {/* Sub Category Tabs */}
            {currentSubCategories.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-8">
                <button
                  onClick={() => setActiveSubCategory('全部')}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                    activeSubCategory === '全部'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-300 hover:text-foreground-100 hover:bg-background-200 border border-background-300/30'
                  }`}
                >
                  全部
                </button>
                {currentSubCategories.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => setActiveSubCategory(sub)}
                    className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                      activeSubCategory === sub
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-300 hover:text-foreground-100 hover:bg-background-200 border border-background-300/30'
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            )}

            {/* Video Grid */}
            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {filteredVideos.map((video, index) => (
                  <div
                    key={video.id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <VideoCard video={video} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-20 text-center">
                <i className="ri-video-line text-5xl text-foreground-600"></i>
                <p className="text-foreground-500 mt-4">该分类暂无内容，敬请期待</p>
              </div>
            )}

            {/* Category Links */}
            <div className="mt-12 pt-8 border-t border-background-300/30">
              <h3 className="text-sm font-semibold text-foreground-200 mb-4">更多分类</h3>
              <div className="flex flex-wrap gap-2">
                {categories.filter(c => c.slug !== 'all' && c.slug !== slug).map((cat) => (
                  <a
                    key={cat.slug}
                    href={`/category/${cat.slug}`}
                    className="whitespace-nowrap px-4 py-2 text-sm rounded-full bg-background-100 text-foreground-300 hover:text-primary-400 hover:bg-primary-500/10 border border-background-300/30 transition-all duration-200 cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}